import { find } from '@execonline-inc/collections';
import {
  defaultSettings,
  element,
  initTask,
  interpolator,
  Interpolator,
  Loaded,
  LoadedFromFallback,
  parameterized as p,
  Parameterized,
  Props,
  scalar,
  translations,
} from '@execonline-inc/translations';
import i18next, * as i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';
import { Maybe } from 'maybeasy';
import { identity } from 'rxjs';
import { Task } from 'taskarian';
import { equalTo } from '../Collections/Predicates';

const languageDetectionOptions = {
  order: ['localStorage', 'navigator', 'querystring'],
};

const defaultI18nSettings: i18n.InitOptions = {
  ...defaultSettings,
  detection: languageDetectionOptions,
  backend: {
    loadPath: `${import.meta.env.VITE_PUBLIC_URL}/locales/{{lng}}/{{ns}}.json`,
  },
};

interface I18nextInitialization {
  initializer: i18n.i18n;
  i18nSettings: i18n.InitOptions;
}

const initialization = (initializer: i18n.i18n): I18nextInitialization => ({
  initializer,
  i18nSettings: defaultI18nSettings,
});

const i18nInitializerT = (): Task<never, I18nextInitialization> => {
  const initializer = i18next.use(HttpApi);

  return Task.succeed<never, i18n.i18n>(initializer.use(LanguageDetector)).map(initialization);
};

export const loader = (fallback: string): Task<LoadedFromFallback, Loaded> =>
  i18nInitializerT()
    .mapError<LoadedFromFallback>(identity)
    .andThen(({ initializer, i18nSettings }) => initTask(initializer, i18nSettings));

const translatablePlainTextKeys = [
  'Add a collection logo',
  'Add to Selection',
  "Addresses that aren't in your organization won't receive an invitation at all.",
  'Button label',
  'Button link',
  'The button only appears when both a label and a link are set',
  'Collection communications - AdminStudio',
  "Choose how participants in this collection are emailed. Anything you customize here overrides your organization's template.",
  'customized',
  'No customizations yet',
  'Collection communications',
  'Manage Communications',
  'Back to Collections',
  'Loading the message this collection sends today',
  'Started from the message this collection sends today',
  'The current message could not be loaded, so this editor starts empty',
  'Coaching invitations',
  'Enrollment reminders',
  'Getting started',
  'Group coaching',
  'Completion',
  'Account',
  'Other communications',
  'Invitations to book coaching, and the reminders that follow while no session has been scheduled.',
  'Invitations, reminders and cancellations for scheduled group coaching sessions.',
  'Triggered by what someone does while learning: live events, coach feedback, chat replies and assignments.',
  'Certificates sent at graduation, and the follow-up nudges to enroll in something next.',
  'Communications whose trigger has not been placed in a theme yet.',
  'Off',
  'Turned off for this collection',
  'Sending to this collection',
  'Participants in this collection are not receiving this message',
  'Turning it off stops this message for this collection only',
  'Turn off',
  'Turn back on',
  'This message is turned off for this collection. Nothing is sent until you turn it back on.',
  'This message cannot be turned off: participants need it to reach their account',
  'This message cannot be turned off: it opens a sequence the later reminders refer back to',
  'This message cannot be turned off for a single collection',
  'Turn this whole section off',
  'Turn this whole section back on',
  'Turn off these messages?',
  'Turn these messages back on?',
  'Participants in this collection will stop receiving the messages in this section.',
  'Participants in this collection will start receiving the messages in this section again.',
  'Messages that cannot be turned off are left as they are.',
  'All off',
  'off',
  'Turn off for this collection',
  'Turn back on for this collection',
  'Customize for this collection',
  'Use the organization version',
  'Use the default version',
  'Change how this message is sent',
  'Change how this section is sent',
  'Edit this message',
  'Turn it back on first',
  'This message cannot be turned off',
  'Course invitations',
  'During the course',
  'Sent before someone starts: the invitation to register for a course or collection, and the reminders that follow if they have not.',
  'Nudges for people who registered but have not yet enrolled in a course.',
  'Welcome and orientation messages sent as a course begins.',
  'Account and sign-in messages, sent outside of any one course.',
  'Communications',
  'Customize the emails participants in this collection receive',
  'Default',
  'Live preview',
  'Revert to inherited',
  'Save override',
  'Saved',
  'Select a communication to customize',
  'Subject',
  'These tokens will not be replaced for this communication',
  'These vary by participant and are empty in this sample',
  'This collection',
  'An error occurred, please refresh the page and try again',
  'All',
  'Are you sure?',
  'Back',
  'Back to Builder',
  'Back to Curated Sections',
  'Back to collection',
  'Browse All',
  'Cancel',
  'Carousel',
  'Choose one of the curated collections below, or create your own. You can edit the final list of programs in the next step.',
  'Continue',
  'Continue with 1 Collection',
  'course needs another license',
  'courses need another license',
  'courses need other licenses',
  'Customize',
  "Email can't be blank",
  'Email Address',
  'Email Address or Username',
  'email@domain.com',
  'Everyone can join, but no one can enroll in every course.',
  'Everyone holds a license.',
  'Everyone is either fully licensed or not licensed at all.',
  'Everyone is fully licensed',
  'Fully licensed',
  'Get Started',
  'In this collection',
  'Leadership Coaching',
  'Licenses decide which courses someone can enroll in.',
  'No license',
  'No one has full access yet.',
  'No one is fully licensed',
  'No one is licensed for these courses',
  'Not held',
  'not in your organization',
  'Not licensed',
  'Not yet started',
  'On-Demand',
  'Partially licensed',
  'Participant licenses',
  'people',
  'person',
  'preview only',
  'Professor-Led',
  'Recheck licenses',
  'Send invitation',
  'Send invitations',
  "They can join the collection but can't enroll in every course.",
  'Your organization holds no license that covers this collection.',
  'Your session has expired. Please login.',
  'You are missing the application id.',
  'An error occurred, please refresh the page and try again',
  'An email has been sent to you to verify your account and reset your password.',
  'You are missing the application id',
  'You are missing the API compatibility version',
  'I forgot my password',
  'Next',
  'Collection Owner',
  'Learning Collections',
  'Learning collections link is unavailable',
  'Learning Designs',
  'Learning designs link is unavailable',
  'Unable to load your learning collection',
  "Let's create your first learning collection",
  'Go to my Learning Collections',
  'Nice work! Your collection is ready.',
  'Visit the Learning Collections page to edit, preview, and share your collection. After launching, you’ll be able to see more insights into how your collection is performing.',
  'Login to Your Account',
  'Go Back',
  'Go to My Learning Collections',
  'Welcome to the Client Portal!',
  'Password',
  'List',
  'Loading curated sections...',
  'Login',
  'No curated sections available. Please check back later.',
  'Programs',
  'Remove',
  'Remove from Selection',
  'Reports',
  'Your Reports',
  'Status',
  'Support',
  'Close',
  'Close Menu',
  'Menu',
  'Error Landing',
  'Error: The app is misconfigured.',
  'error_page.not_found.go_to_dashboard',
  'error_page.not_found.message',
  'error_page.not_found.title',
  'Unable to connect to the ExecOnline platform.',
  'Unable to establish a session for this application.',
  'You are not authorized to access this application.',
  'Please contact your learning and development team in order to access Client Portal features.',
  'Turning off this setting will remove the full catalog section from your collection.',
  'Toggle Menu',
  'Account Navigation Dropdown Menu',
  'Account Navigation',
  "We're sorry, but we couldn't find what you were looking for.",
  'Back to previous page',
  'Go to Learning Collections',
  'Need Assistance?',
  'If you need additional help, you can ...',
  'Participants can browse the full catalog',
  'EXO AI Assistant Logo',
  'Your request could not be completed because the url was malformed.',
  'Your request took too long to complete.',
  'You are disconnected from the server.',
  'Display Type',
  'Drag programs here',
  'Download',
  'Drop here to add',
  'Your request failed.',
  'The server responded with unexpected data.',
  'less than a minute from now',
  'less than a minute ago',
  'a minute from now',
  'a minute ago',
  'about an hour from now',
  'about an hour ago',
  'a day from now',
  'a day ago',
  'about a month from now',
  'about a month ago',
  'about a year from now',
  'about a year ago',
  'value was null or undefined',
  'loading content',
  'Required',
  'Sign In via Learning Platform',
  'Status',
  'Failed to store necessary authentication data in the browser.',
  'Failed to redirect the browser to the authorization request proxy.',
  'Log Out',
  'Stop Impersonating',
  'Stopping…',
  'Admin impersonation notice',
  'You have been logged out',
  'If you would like to log in again, click the button below.',
  'Logging you in...',
  'Your Client Portal account is accessed via your Platform account.',
  'You will automatically be returned to the Client Portal once you are signed in to your Platform account.',
  'Stay Logged In',
  'Logging Out Soon Due to Inactivity',
  'Your session has expired, please log in again.',
  'Report ID is not present',
  'Report link is unavailable',
  'Reports link is unavailable',
  'Enhance your collection...',
  'Add-Ons',
  'more detail',
  'Create a title and...',
  'Describe your collection',
  'You have no reports',
  'Dashboard not found',
  'Unable to load report',
  'Usage Details',
  'Visualization token link is unavailable',
  'Beta',
  'All rights reserved',
  'My Profile',
  'User Photo',
  'Enterprise Portal',
  'Switch to',
  'Platform Navigation Dropdown Menu',
  'Metrics',
  'Experiences',
  'Student members',
  'Last registration date',
  'Get started by adding your first Learning Collection',
  'Learning Collections are curated sets of experiences ...',
  'Active',
  'Draft',
  'Save and Close',
  'Name A to Z',
  'Name Z to A',
  'Last Updated: Newest First',
  'Last Updated: Oldest First',
  'Sort by',
  'Edit',
  'Preview',
  'Select',
  'Select a curated section',
  'Share',
  'Publish and Share',
  'Published',
  'Name',
  'Description',
  'Title',
  'Owner',
  'Internal Name:',
  'Total Hours',
  'School Partner',
  'appears at the top of the page on the main participant landing page',
  'appears below the description on the main participant landing page',
  'In Partnership With',
  'View all programs →',
  'University partner logo',
  'Finished Placeholder',
  'Share Placeholder',
  'Preview Placeholder',
  'Share Your Learning Collection',
  'Publish and Share',
  'Next',
  'Prev',
  'Previous',
  'Pagination',
  'Return to Learning Collections',
  'Return to Collections',
  'Publish your Learning Collection to create a URL you can share with participants.',
  'Learning Collection Preview for Participants',
  'Video Project Feedback',
  'Each time a participant completes a...',
  'Included Learning Designs',
  'Collaborate with your team to ...',
  'No Learning Collections found',
  'Includes all experiences',
  'Please check your spelling or modify your search',
  'View and download data insights ...',
  'Create Learning Collection',
  'Edit Participant View',
  'Leave Edit Mode',
  'Save & Reload Preview',
  'Unsaved Changes',
  'You have unsaved changes. Please...',
  'Discard Changes',
  'Keep Editing',
  'Save Changes',
  'Launch Content Editor',
  'Done',
  'Edit Description',
  'loading participant preview...',
  'Learning',
  'Organization',
  'Client Group',
  'Private',
  'Loading...',
  'Type to search...',
  'AdminStudio',
  'Coaching',
  'Please modify your search',
  'Reset all filters',
  'Offered by',
  'School Partners',
  'Download Overview',
  'Start designing your collection',
  'Start building your collection',
  'Choose one of the curated collections below, or create...',
  'Customize Program List',
  'Time Commitment',
  'Partner',
  'Competencies',
  'Competency',
  'Remove this Collection?',
  'Cancel',
  'Removing',
  'Remove from List',
  'Add program',
  'Add Program',
  'Add courses',
  'View full details',
  'Add programs',
  'Program Catalog',
  'Competency',
  'Choose a set of programs from our catalog...',
  'Language',
  'Trending',
  'Program Deep Link',
  'Program Deep Links',
  'No Program Deep Links to display',
  'Delivery Channel',
  'Build your own collection',
  'Programs contain the core learning content consumed by...',
  'Programs',
  'Remove program',
  'Managed by ExecOnline. Edits not supported in AdminStudio.',
  'Learning designs URL is invalid',
  'Edit Program Overview',
  'Discard changes',
  'Include additional framing for the programs in this collection',
  'Save',
  'Theme',
  'Flexible',
  'Essentials Catalog',
  'Along with your curated collection, participants ...',
  'The Essentials catalog includes 100+ high-impact ...',
  'Add all filtered programs',
  'Deep Link Enabled',
  'Add Deep Links',
  'Copy Deep Link',
  'Access Options',
  'Use Program Deep Links to promote specific programs in this collection...',
  'Add New Program Deep Links',
  'Add New Course Deep Links',
  'Shareable Links',
  'Shareable Link',
  'No Shareable Links to display',
  'Download All Course Links',
  'Copy Collection Link',
  'Copy Course Link',
  'Channel',
  'Enabled',
  'Disabled',
  'Link active',
  'Link inactive',
  'Professor-Led Course',
  'Program Sequence',
  'Program Deep Links are direct links ...',
  'A license is considered Activated when a...',
  'A license is considered Allocated when a...',
  'A license is expiring in less than 90 days',
  'Activated',
  'Activations are restricted when the number of...',
  'Allocated',
  'Allocations are not restricted and may exceed...',
  'Available to Activate',
  'Ending soon',
  'Expired',
  'Invite more participants to collections',
  'License Insights',
  'License Status',
  'License Usage Overview',
  'License Usage to Date',
  'Download CSV',
  'Licenses Activated',
  'Licenses Available',
  'Licenses Purchased',
  'Maximize your opportunities and plan ahead...',
  'Mixed',
  'Product Licenses',
  'Product',
  'Products',
  'View the up to date status for all licenses...',
  'View',
  'Allocate Licenses',
  'Manage Licenses',
  'License(s) to Manage',
  'License(s) to Allocate',
  'License Allocation Successful',
  'You allocated the following license(s):',
  'Allocate License to Participant',
  'Manage Licenses for Participants',
  'Allocate License to Group Members',
  'Select the licenses you want to allocate.',
  'Allocate or release licenses for the participants below.',
  'Select the license you want to allocate the groups...',
  'Error submitting form.',
  'Your organization currently has no available licenses to...',
  'This group has no participants...',
  'No licenses are available for allocation to this...',
  'Error Loading.',
  'Participants will not be notified when licenses are...',
  'After allocating, remember to invite participants to...',
  'Activated licenses are ineligible to release. After releasing...',
  'Group members are not notified when licenses are allocated...',
  'participant',
  'participants',
  'Participant',
  'Participants',
  'No Participants',
  'All Licenses',
  'No participants found',
  'License Alerts',
  'View and resolve any issues before inviting participants.',
  'Loading license alerts...',
  'License Period',
  'Candidates for License Upgrade',
  "Tip: Go to People to upgrade participants' licenses if you want them to have full VIP access.",
  'Unrecognized Participants',
  'Tip: Go to People to add or import unrecognized participants before inviting them to a collection.',
  'Refreshing...',
  'Refresh',
  'Information about license period',
  'Soonest End Date:',
  'Latest End Date:',
  'Share with Participants',
  'Choose how to share, then confirm more details in the next step.',
  'Invite Participants to Collection by Email',
  'Invite Participants to Collection',
  'Invite Participants to Course by Email',
  'Invite Participants to Course',
  'Inviting to',
  'Confirm Participants',
  'Search Group Name',
  'No groups found',
  'Enter or paste names, emails or group names to invite to this collection.',
  'Enter or paste names, emails or group names to invite to this course.',
  'Toggle schedule invitation',
  'Send a Message',
  'Set an Invitation Time',
  'Set the date and time to email participants their invitation to this collection.',
  'Set the date and time to email participants their invitation to this course.',
  'Optionally email participants an invitation at a scheduled time. They get access either way.',
  'Allow Participant Access',
  'Information about license upgrades',
  'Currently has',
  'Information about unrecognized participants',
  'Send Invitations',
  'Add selected participants',
  'Select Participants',
  'Share by Email',
  'Share by URL',
  'What would you like to share?',
  'Collection access (participants start by browsing the collection)',
  'Program access (participants start in a specific program)',
  'People',
  'Filter',
  'Preferred Language',
  'Last Login',
  'Segments',
  'Administrators',
  'Collections',
  'Full Name',
  'Email',
  'Login Count',
  'Group Count',
  'Search Name or Email',
  'Collection Membership',
  'We are unable to process your request at this time',
  'Product Type',
  'On-Demand Learning',
  'Advanced Leader Programs',
  'New Manager Essentials',
  'Individual Leadership Coaching',
  'Group Coaching',
  'Sequence',
  'All',
  'Load More',
  'Add Participants to Group',
  'Participants to be added to group',
  'Submit',
  'Participants Added',
  'Select Group',
  'Select group',
  'You need at least one group in order to...',
  'Back to your Learning Collection',
  'Groups',
  'Program Family',
  'No Groups',
  'Group Name',
  'Last Updated At',
  'Login Activity',
  'Search Participant Name',
  'Available Start Dates',
  'On Demand',
  'Advanced Leader Program',
  'Select Start Date(s)',
  'Start Dates',
  'AEP',
  'ALP',
  'Show fewer',
  'Import participants',
  'Import Participants',
  '+ Add participant',
  'Confirm Participant',
  'Confirm',
  'A valid email address is required',
  'Email domain is not available for this organization; contact support if you need further assistance',
  'Email address not available for this organization; contact support if you need further assistance',
  'Your collection title will appear here',
  'Imported participants can later be added to groups, ...',
  'Collection description will appear here',
  'Upload file',
  'Download sample.csv file',
  'If the preview of your upload looks accurate...',
  'Upload a new file',
  'Table Preview',
  'Back to all people',
  'Assign a license',
  'Invite to collection',
  'Add to group',
  'What would you like to do next?',
  'Import Successful',
  'Report Table',
  "Drag 'n' drop your csv file here ...",
  'unconfirmed',
  'Import participants link is unavailable',
  'Import options link is unavailable',
  'The list should not exceed 500 rows',
  'First name',
  'Last name',
  'Email address',
  'Reason',
  'Source file was empty or in the wrong format',
  'There was an error formatting the CSV file',
  'Only use CSV files.',
  'Only use CSV. Could not process this file',
  'Update Start Dates',
  '(Note: participants may only enroll in one-date per program)',
  'Select one or more dates for participants to choose from when enrolling.',
  'Program',
  'Built for busy leaders, our library...',
  'Bring the best of business school...',
  'Leadership Coaching experiences can address ...',
  'Coaching',
  'For leaders that would benefit from having a personal guide ...',
  'Unlock your team’s potential with our purpose-built development paths...',
  'Group',
  'Find by name and email',
  'Add Participants',
  'Add participants to the group by finding them in the search field below.',
  'Updating licenses for',
  'Selected participant(s)',
  'Groups to be Allocated',
  'Start adding participants by searching them in the field above.',
  'Submit',
  'Participants Added',
  'Type an email address to invite a new participant.',
  "You haven't added any participants to this group. Add a participant to get started.",
  'No participants in this group match your criteria. Please revise your criteria to view participants in this group.',
  'Manage Group Members',
  'Add Participant',
  '+ Add Group',
  'Create a New Group',
  'Describe your new group. You will be able add participants on the next step.',
  'Inactive groups cannot be invited to collections.',
  'Group Successfully Created',
  'What would you like to do next?',
  'Add Participants to this Group',
  'Add Another Group',
  'Back to Group',
  'Back to Groups',
  'Edit Group',
  'Describe your group. You will be able add participants on the next step.',
  'Group Successfully Edited',
  'Group Edited',
  'Program Type',
  'Copy Collection URL to Share with Participants',
  'Invite Participants and Groups to Collection',
  'Learn more about sharing with participants',
  'Select the participants and groups to invite.',
  'Invite to Collection',
  'Select Participants or Groups',
  'Enter Participant Name, Email or Group Name',
  'Collection',
  'When should we send it?',
  'Set the time and date to send participants invitations for this collection',
  'Preview the message and learn more about invitations',
  'Invitations Scheduled',
  'Invitations Partially Scheduled',
  'Invite More Participants',
  'Choose a Course',
  'Invite More to This Collection',
  'Invite More to This Course',
  'Invite to Another Course',
  'Course Start',
  'Pick the course to invite participants to.',
  'Start anytime',
  'Start date',
  'Start dates',
  'Search courses',
  'No courses match your search.',
  'Return to Collection',
  'Some participants could not be added',
  'Unknown participant',
  'Timezone',
  'Time',
  'Date',
  'Send email date must be in the future',
  'Error loading options',
  'Start typing to load options',
  'Publish your collection for participants to access...',
  'Product licenses link is unavailable',
  'Start by defining your preferences for this collection ...',
  'Choose the license aligned with your leadership development goals.',
  'Your selection determines the programs, resources, and ...',
  'More details about this license',
  'Who is it for?',
  'Includes following content',
  'Select a curated section',
  '[Your Section Goes Here]',
  'Use sections to help organize programs for participants and accelerate their discovery.',
  'Sections help you organize programs into a curated learning path. Add your first section to get started.',
  'Add a section to start adding programs.',
  'Add a section',
  'Start with a blank section',
  'Pick your own programs and build from scratch',
  'Start with Curated Sections',
  'Add to collection',
  "They won't receive an invitation until someone assigns them a license.",
  "Everyone invited can join and preview the courses but can't enroll.",
  'Everyone invited is assigned a license automatically.',
  "Everyone invited can join the collection but can't enroll in anything.",
  'No license you can assign covers this course.',
  'Unlocked with this license:',
  'Unlocked with any one of these licenses:',
  "They aren't in your organization, so they won't receive an invitation.",
  "They won't receive an invitation: some hold no license, some aren't in your organization.",
  'Add a section description',
  'Add programs to this section',
  'Button Label',
  'Change Section Display',
  'Choose how your section displays to participants.',
  'Close dialog',
  'Confirm Section Delete',
  'Delete this section',
  'Display Style',
  'e.g., View Programs',
  'Find courses to add',
  'Optionally hide your section by default; participants click a button to view the section. If blank the section will display directly.',
  'Remove Program',
  'Remove Section',
  'Remove section',
  'Scroll left',
  'Scroll right',
  'Section display type',
  'Section settings',
  'Add course',
  'Add to this section',
  'Added to this section',
  'Available Course Dates',
  'Build your learning collection by selecting courses aligned to your talent objectives.',
  'Choose a course from the list to view full details',
  'Clear filters',
  'Close filters',
  'Close video',
  'Competencies you will gain',
  'Competency Coverage',
  'Course Catalog',
  'Course Type',
  'Download course overview',
  'Download course overview PDF',
  'Download PDF',
  'Featured',
  'Filters',
  'Included in this section:',
  'No courses found',
  'Overview',
  'Press ESC or click outside to close',
  'Remove course',
  'Search courses, schools, competencies...',
  'Quick add courses',
  'Other',
  'Select a course',
  'Show unlicensed courses',
  'Toggle unlicensed courses',
  'Try adjusting your search or filters',
  'Unlicensed',
  'Preview video',
  'Watch Preview',
  'Failed to load overview video',
  'School partner logo',
  "This course is not included in your organization's current licenses. You can add it to preview, but learners won't have access without purchasing the appropriate license.",
  'Your browser does not support the video tag.',
  'Add the full catalog of 120+ On-Demand Courses',
  'Already added',
  'Blank',
  'Browse pre-built sections based on popular themes',
  'Change display type',
  'Curated',
  'Item',
  'Quick Start',
  'Start with an empty section and add specific courses',
  'Choose from an extensive library of themes...',
  'Programs will show up here',
  "You haven't added any programs to this collection...",
  'View all programs',
  'Includes following content icon',
  'A valid product license is required to build ...',
  'Confirm License Update',
  'Please confirm if you would like to continue.',
  'Unlicensed',
  'Not Licensed',
  'Never',
  'Contact your ExecOnline representative to add licenses.',
  'Participant Invitations',
  'Invited Participants',
  'Invitation Type',
  'Share URL',
  'Copy URL',
  'Advanced Settings',
  'remaining - ends on',
  'Block access',
  'Allow read-only access',
  'Allocate a license',
  'Failed to save advanced settings. Please try again later.',
  'Cannot save advanced settings in the current state.',
  'Learning collection resource not loaded.',
  'No share URL available for this collection.',
  'Unable to load program-specific URL. Showing collection URL instead.',
  'Preview Only',
  'License',
  'Licenses',
  'Auditor',
  'Total Enrollments',
  'Invitation Status',
  'Access Status',
  'Yes',
  'No',
  'Invitations Sent',
  'No Results',
  'Invited participants will appear here',
  'Invite some participants and/or groups to get started.',
  'Invite Participants',
  'Remove participant',
  'Return to Participants',
  'No Scheduled Dates',
  'Some participants allocated a license may not be able to enroll.',
  'All participants must belong to the same org when bulk license provisioning',
  'Select the group you want to add the following recipients to',
  'No Scheduled Dates',
  'Select the group you want to add the following recipients to',
  'Some participants allocated a license may not be able to enroll.',
  'All participants must belong to the same org when bulk license provisioning',
  'Last Invitation Date',
  'Not emailed',
  'Scheduled for',
  'Remove Collection Access',
  'Restore Collection Access',
  'This participant will no longer be able to...',
  'The selected participants will no longer be able to...',
  'Collection Access Removed',
  'Collection Access Restored',
  'Need help getting started?',
  'Last Invitation Send Date',
  'Collection Access',
  'Total Invitations',
  'Accepted',
  'Pending',
  'Program Access',
  'Manage Participant Access',
  'Collection not Published',
  'Publish Collection',
  'Publish for Student',
  'Save for Later',
  'Switch to Editor',
  'Nice work! The collection is ready for your student.',
  "Refresh your Coaching Profiles page to preview, edit, or update the student's access to the collection.",
  'Close this tab',
  "Now that's progress - your collection is saved for later.",
  'Invite',
  'Invite to Program',
  'When should they start?',
  'Confirm the invitation schedule for the selected participants.',
  'Confirm the start date for the selected participants.',
  'License Type',
  'License Started Date',
  'Create',
  'Product License',
  'No product license selected',
  'Choose Start Date',
  'Update the license associated with this learning collection to see new available dates...',
  'Development Path',
  'Dev Path',
  'Group Coaching (not testable)',
  'Features',
  'ODL',
  'Add-on',
  'Invitations are not available for this collection. Please contact your ExecOnline representative to learn more about options for inviting participants.',
  'User already invited',
  'User already enrolled',
  'Invitations are not available for this collection. Please contact your ExecOnline representative to learn more about options for inviting participants.',
  'Home',
  "Here's a snapshot of what’s been happening in your organization.",
  'Follow the steps below to get your first collection up and running.',
  'No Representative Assigned',
  'You do not have a designated account representative. Please contact',
  'No activity yet',
  'It seems like there haven’t been any recent activities in your organization yet.',
  'Your data will appear here when it is available.',
  'See more',
  'View details',
  'In the last 30 days',
  'Develop these Competencies',
  'In the current license term',
  'In published collections',
  'Create a Learning Collection',
  'Publish a Learning Collection',
  'Invite Participants to a Program in a Learning Collection',
  'Onboard Participants to a Learning Collection',
  'Add Group',
  'Create Group',
  'Creating...',
  'Saving...',
  'Group Created',
  'Return to Groups List',
  'Group Details',
  'associated',
  "Give the group a name, an optional description, and set whether it's active.",
  'e.g. Q1 2026 Cohort',
  'What is this group for?',
  "Inactive groups keep their members and history but can't be assigned new licenses or collections.",
  'Groups let you assign licenses and collections to many people at once.',
  'Could not load new-group defaults. Please refresh.',
  'Could not create the group. Please check your entries and try again.',
  'Add Groups',
  'Add/Import Participants to Groups',
  'Generate Program Deep Links for a Learning Collection',
  'Create a Learning Collection for Each of Your Product Licenses',
  'Admin Tasks',
  'Overall Engagement',
  'Recently Logged In',
  'Popular Competencies',
  'Participant Milestones',
  'License Insights',
  'Enrollments',
  'Active Participants',
  'Activated Licenses',
  'Programs In Use',
  'License insights will appear here once they are available.',
  'No Longer with Company',
  'Remove Access',
  'Remove login and collection access. Any licenses will be returned for reuse.',
  'Impacted Participants',
  'Access Removed',
  'Participant is no longer with the company',
  'Program types available for this collection will be shown here when you start adding programs',
  'Choose a license to start adding programs to this collection',
  'or',
  'Your CSV File is Ready',
  'Download',
  'Download Program Deep Links in CSV',
  'Some invited participants already have a license.',
  'If all participants enroll you may exceed the available activations.',
  'Invite more participants!',
  'More Offerings from ExecOnline',
  'Your organization has no email domains...',
  'Invitations Created',
  'Participants will gain immediate access and no email will be sent to the invited participants.',
  'Send Invitation Email to Invited Participants',
  'Type at least 3 character to load options',
  'Grid',
  'Section Button Text (optional)',
  '0 programs included;...',
  'Section Display Type',
  'Edit Section Details',
  'Add Essentials Catalog',
  'Add the ExecOnline Essentials catalog',
  'Collection participants can browse...',
  'Add the ExecOnline Essentials catalog [already included]',
  'Last Collection Access Date',
  'Upload Image',
  'Upload an image file (PNG, JPG, GIF) that is 5mb in size or less',
  'Replace Image',
  'Delete Image',
  'Failed to upload the image.',
  'Unable to get signed url',
  'File url error',
  'Failed to upload the file to s3',
  'I hit a snag generating the collection preview with updated recommendations. Retrying now...',
  'Organization Logo',
  'Proceed',
  'You don’t have enough licenses for this program type',
  'You may still add this program to your collection right now and will not be charged for any additional licenses at this moment.',
  'Participants will only gain access if you obtain the right license. Otherwise their access to this program will be restricted.',
  'Once it’s part of your collection, you may also hide unlicensed programs from your collection and activate them at a later point.',
  'Learn more by talking to your ExecOnline Rep or by clicking here.',
  'Do not show me this message again',
  'Show Unlicensed Programs',
  'Create Learning Collection V2',
  'Edit (classic)',
  'No programs added yet',
  'Click to start adding programs to this section',
  'Create a collection with EXO AI',
  'Multi Step Learning Journey',
  'Scheduled Program',
  'On-Demand Program',
  'Hello! I’ll guide you through a series of questions to understand your leadership development goals, and recommend programs from ExecOnline’s catalog that align with those goals.\n\nAs we move along I’ll preview the recommended programs in a learning collection for your leaders, and generate a welcome message based on your specific collection theme(s).\n\nI strive to provide accurate and helpful guidance, but as an AI advisor may occasionally miss the mark. Please let me know if this happens, and I will do my best to get back on track.\n\nYou can save the learning collection at any point to conclude our chat and proceed with onboarding your leaders, or continue refining the collection manually.\n\n<b>Shall we begin?</b>',
  'Hello! I am EXO, your AI coach advisor, here to assist you in designing a learning collection for any individual or cohort of leaders.\n\nLike a human ExecOnline advisor, I will have a conversation with you to better understand your audience’s leadership development goals, then recommend programs from ExecOnline’s catalog that align to the goals and preview the recommendations in a learning collection that is ready to publish for participants.\n\nOnce the programs are confirmed I can also generate a welcome message for participants based on your specific collection theme(s).\n\n<b>Would you like to get started?</b>',
  'Select a License',
  'EXO will only recommend programs that are included with the selected license.',
  'remaining',
  'Starts',
  'Expires',
  'Your organization logo will appear here',
  'Participant Preview',
  'Build a learning collection in minutes with AI',
  'Choose How to Build Your Learning Collection',
  'Customize all aspects of your collection...',
  'Invest more time',
  'Chat with our AI Learning Advisor',
  'Let EXO - your AI advisor - do the heavy lifting by tailoring a collection to your objectives. Great for admins seeking recommendations and guidance while building.',
  'Create in minutes',
  'Use the Collection Builder',
  'Use Classic Builder',
  'What is a Learning Collection?',
  'A Learning Collection is a curated suite of programs organized into one or more sections, with each section including programs focused on your specific objective(s). You can customize your learning collections and share them with specific individuals or groups, making it simple to manage and deliver your talent initiatives.',
  'Learn more about learning collections',
  'Import Partially Successful',
  'Import Not Successful',
  'View all details',
  'Download full CSV',
  'Back to confirmation screen',
  'Enroll',
  'You can not enroll in Preview Mode',
  'Do you want to update to latest draft?',
  'Apply Draft',
  'Something went wrong',
  'Not active in preview mode',
  'Search and filters not active in preview mode',
  'Search in this collection',
  'loading learning collection template...',
  'oops learning collection failed!',
  'EXO, AI Assistant',
  'Your AI Advisor',
  'EXO',
  'Refreshing Collection Preview',
  'Please wait while we load the changes',
  'As an AI advisor I may make mistakes. Please be patient and submit feedback often. Your feedback will help me learn.',
  'Your organization logo is displayed here - you can change or remove this logo by editing the collection later',
  'Discard this collection',
  'If you leave now the collection will not be saved and cannot be restored later. Are you sure you want to continue?',
  'Publish this collection?',
  'Create this collection?',
  'Save this collection?',
  'Publishing the collection will end the session with EXO and you will not be able to resume the same chat again later - future edits to the collection must be done manually. Are you sure you want to continue?',
  'Creating the collection will end the session with EXO and you will not be able to resume the same chat again later - future edits to the collection must be done manually. Are you sure you want to continue?',
  'Saving the collection will end the session with EXO and you will not be able to resume the same chat again later - future edits to the collection must be done manually. Are you sure you want to continue?',
  'Publishing the collection will finalize your changes and make the collection available to participants. Are you sure you want to continue?',
  'Creating the collection will save your work as a draft. Are you sure you want to continue?',
  'Create without Publishing',
  'Save & Switch to Manual Editing',
  'Preview with License Type',
  'Save Collection',
  'Create Collection',
  'Edit Collection',
  'Create a collection - AdminStudio',
  'Edit a collection - AdminStudio',
  'Your collection title is displayed here - EXO can generate the title and you can also change or remove the title by editing the collection later',
  'Low Effort',
  'High Effort',
  'Upcoming Start Date',
  'There are no license available for your organization. You must have a license to continue.',
  'Your admin is not permitted to build learning collections. Builder access is required to continue.',
  'You must have an organization license and builder access to continue. Contact ExecOnline for assistance.',
  'Collection Published',
  'Your collection has been published and active. You may now invite participants.',
  'Creating your Collection',
  'Hang tight — this may take a minute or two',
  'Published just now',
  'Your collection has been created but is not published. You may now preview the unpublished collection with other admins, or publish the collection to invite participants',
  'Collection Created',
  'Create a Collection',
  'Clone',
  'Cloning your collection',
  'Duplicating collection...',
  'Copying courses...',
  'Organizing sections...',
  'Finalizing...',
  'Collection cloned',
  "Some courses from the original collection couldn't be copied automatically. Review this collection's courses before publishing.",
  'Publish & invite participants',
  'Edit collection',
  'Clone failed',
  'Unable to clone this collection. Please try again later.',
  'Manage Collaborator Access',
  'Collaborator access updated just now',
  'All admins in your organization can see and edit this collection.',
  'Only you can see and edit this collection.',
  'Only members of the groups you select can see and edit this collection.',
  'Select at least one group.',
  'Unable to update collaborator access. Please try again later.',
  'Unable to load collaborator access. Please try again later.',
  'Unable to load learning collections. Please try again later.',
  'I’m here to assist you in creating a learning collection tailored to any cohort of leaders.',
  'Your feedback is invaluable in helping me improve - please share your thoughts and suggestions frequently!',
  'EXO, AI Advisor',
  'Something went wrong and the collection was not saved. Please try again.',
  'Add Internal Name',
  'Enter the email and other details for the participant you are adding',
  'Add Participant to Groups',
  'Group the participant to find and report on later',
  'Please select a group',
  'Choose License(s) to Allocate',
  'Choose one; only choose more than one if you are allocating Core + Add-on licenses and/or for consecutive terms license',
  'Select a license if needed...',
  'ends on',
  'Set as Auditor Licenses',
  'Auditor licenses are intended for admin and stakeholder review',
  'Enter participant details, add them to a group and assign them a license',
  'Participant Saved',
  'Allocated Licenses',
  'Add Another Participant',
  'Return to Participant List',
  'First name is required',
  'Last name is required',
  'Search groups or type new group name...',
  'Members will be allocated any license(s) you select here.',
  'License Update Successful',
  'Last Collection Email Date',
  // Group & License Selection Sections (shared by Add Participant + Import Participants V2)
  'Optionally add to groups for collection access, license allocation, and reporting.',
  'Search groups...',
  'Create New Group',
  'New group name',
  'Enter group name',
  'Clear all',
  'No groups yet',
  'Groups let you organize participants for collection access, license allocation, and reporting.',
  'Create Your First Group',
  'New',
  'Will be created on submit',
  'Optionally allocate licenses. Licenses associated with groups are selected automatically.',
  'Some licenses pre-selected from groups',
  'The selected groups include associated collections and licenses. These licenses have been pre-selected below.',
  'Search licenses...',
  'No licenses available',
  'There are no active licenses. Contact your ExecOnline account team to add more licenses.',
  'None available',
  'Available',
  '(Low)',
  'Auditor License',
  'Set as an auditor. Auditors can preview courses and collections for review purposes and do not count towards your license activations.',
  'Search collections...',
  'No learning collections yet',
  'Choose which learning collections members see in the Learning platform.',
  'Learning collections give participants a curated set of courses to work through.',
  'Launch participant preview',
  // Import Participants V2
  'Confirm Participant(s)',
  'Import a CSV file including the names and emails for the participant(s) you are adding.',
  'No participants added yet',
  'Upload CSV',
  'Download Template',
  'Participants to add',
  'Add Participants to a Group',
  'Group these participants to find and report on later.',
  'Select a group...',
  'Choose License(s) to Allocate',
  'Licenses to allocate',
  'Import participants, add them to a group and assign them a license.',
  'Confirm',
  'Participants Saved',
  'total participants',
  'Added to Groups:',
  'Allocated Licenses:',
  'Newly Added',
  'Updated',
  'Failed',
  'In Different Organization',
  'Domain Mismatch',
  'No groups are available. Create a group first from the Groups tab.',
  'No licenses are available for allocation. Check the Products tab for more information.',
  'Add More Participants',
  'Return to Participant List',
  'Error loading import options. Please try again.',
  'Retry',
  'Importing participants...',
  'This may take a moment',
  'Error importing participants',
  'System Managed',
  'Managed by ExecOnline',
  'Personalized recommendations will be shown to participants based on their profile.',
  'Dynamic recommendations will be shown based on participant activity.',
  'Group coaching sessions will be displayed to eligible participants.',
  'Promotional content may appear',
  'Based on participant attributes and segments, promotional content may be displayed at the top of the discovery portal.',
  'Generate AI Summary',
  'Generating AI summary...',
  'Generating AI Action Plan Impact summary...',
  'AI Summary',
  'Generated at',
  'Failed to generate summary',
  'Try Again',
  'Action Plan Impact Summary',
  'No action plan data found for the selected date range. Try adjusting the dates to see results.',
  'View Full Report',
  'Adjust date range',
  'Regenerate summary',
  'Regenerate analysis',
  'Copied!',
  'Copy to clipboard',
  'AI Rationale',
  '(No data available)',
  'Try again',
  'Source:',
  'From',
  'To',
  'Regenerate Insights',
  'AI Action Plan Analysis',
  'Summary',
  'Full Analysis',
  // Builder V2
  'Add a collection description',
  'Add a section description',
  'Add an internal name',
  'Edit description',
  'Enter description...',
  'Enter product collection description...',
  'Internal name',
  'Internal name for this collection',
  'Course',
  'Courses',
  'Create a new collection',
  'Edit logo',
  'Remove logo',
  'Remove Logo',
  'Help',
  'Included in this Collection',
  'Name is required',
  'Name your collection',
  'New Learning Collection',
  'On-Demand Course',
  'Save options',
  'Scheduled Course',
  'Section',
  'Sections',
  'This field is required',
  'Upload successful!',
  'Uploading...',
  'Uploaded logo preview',
  'Organization logo',
  'Collection title',
  'Product Collection Name',
  'Edit product collection title',
  'Section title',
  // Builder V2 — Sections Drawer
  'date',
  'dates',
  'Drag to reorder, expand sections to view courses',
  'No courses added yet. Click "Add courses" to begin.',
  'No courses in this section',
  'No sections added yet. Add a section to get started.',
  'Not Available',
  'Sections & Courses',
  'Some programs will not be visible to learners',
  'This program type is not included in your current license entitlement',
  'View Details',
  "Your organization's license does not allow access to all programs in this group.",
  'Add your first section',
  'Collections are organized into sections — use them to group courses by theme, skill area, or learning phase, and to separate required from recommended.',
  'In addition to your section(s) participants will also be able to access to personalized and promoted courses from ExecOnline.',
  '(setup incomplete)',
  '1 domain assigned',
  'A SAML 2.0-capable provider - Entra ID, Okta, ADFS, PingFederate, and others all work',
  'A name your team will recognize - it appears in the domain assignment list. For example "Corporate Entra ID" or "Contractor Okta".',
  'ADFS',
  'About 20 minutes on the IdP side; you can pause and come back anytime',
  'Action needed: configure your identity provider with the values below',
  'Active Directory Federation Services',
  'Add an identity provider',
  'Add identity provider',
  'Admin access to your identity provider (or an IdP administrator on hand)',
  'All of your domains currently use password sign-in. Assign them to identity providers after you add one.',
  'All required attributes matched',
  'Any SAML 2.0-compliant provider',
  'Apply mapping & re-test',
  'Assertion Consumer Service',
  'Assign access on your side.',
  'Assign or change domains from the Single Sign-On page.',
  'Assigned domains',
  'Attributes',
  'Attributes from your last test sign-in',
  'Attributes need attention',
  'Back to connection overview',
  'Before you begin',
  'Brand the app tile',
  'Change the mapping here on the fly - no changes needed on your side - then re-test to confirm.',
  "Changes take effect immediately for new sign-ins. Connections still waiting on IdP setup can't be selected yet. Domains without a connection use password sign-in.",
  'Come back with your metadata URL',
  'Configure your IdP',
  'Configure your IdP with the details we generate',
  'Configure your identity provider application',
  'Connection',
  'Connection ID',
  'Connection details',
  'Connection name',
  'Continue setup',
  'Create connection',
  'Created',
  'Delete this connection',
  'Deleting removes this connection from ExecOnline. Unassign its domains first - people on them return to password sign-in.',
  'Domain',
  'Domains',
  'Each login domain chooses one sign-in method: an identity provider connection, or password sign-in.',
  'Employee ID',
  'Entity ID / Audience URI',
  'Environment',
  'ExecOnline field',
  'Finish the connection',
  'Formerly Azure AD',
  'Get started',
  'Good to know',
  'Green means done.',
  'How claims from your IdP map to ExecOnline profiles. If your IT team changes claims on their side, run diagnostics to re-check.',
  'How this works',
  'Identity provider',
  'Identity providers',
  'In ADFS, add a Relying Party Trust for ExecOnline and enter these values:',
  'In Entra ID, create an Enterprise application, choose Set up single sign-on with SAML, and enter these values in Basic SAML Configuration:',
  'In Okta, create a SAML 2.0 app integration and enter these values:',
  'In PingFederate, create an SP connection for ExecOnline and enter these values:',
  'In your identity provider, create a SAML 2.0 application and enter these values:',
  'Last test',
  'Let your team sign in with your company credentials. Add one or more identity providers, then choose which of your login domains use each one.',
  'Login domains',
  'Login domains are verified and added by ExecOnline - contact support to add one.',
  'Manage',
  'Matched',
  'Metadata URL',
  'Metadata verified',
  'Microsoft Entra ID',
  'Mobile is supported - keep it working.',
  "Most connections are Production. Choose Staging / test if this connection is for your IdP's test tenant - it keeps the connection clearly labeled everywhere.",
  'Needs attention',
  'No domains are assigned to this connection.',
  'No domains assigned',
  'No matching claim received. Add employeeNumber in your IdP (recommended - it keeps accounts stable when email addresses change), or continue without it.',
  'No matching claim received. Add the claim in your IdP, or map a different claim below, then run another test.',
  'No test sign-in has been run for this connection yet.',
  'None assigned yet',
  'Not mapped',
  'Not provided',
  'Not sent',
  'Okta',
  'Okta Workforce Identity',
  'Once your IdP app is set up, paste the federation metadata URL it generated. We finish the connection automatically.',
  'Open attribute diagnostics',
  'Optional',
  'Other SAML 2.0',
  'Participants sign in from the ExecOnline mobile app too. Make sure your IdP policies (conditional access, device requirements) allow sign-in from mobile devices.',
  'Password sign-in',
  'People from assigned domains who sign in for the first time get an account automatically (just-in-time provisioning). They only see content they are enrolled in.',
  'Pick your vendor - Microsoft Entra ID, Okta, or another SAML 2.0 provider. We create a dedicated connection right away. You can add more than one.',
  'Ping Identity',
  'PingFederate',
  'Prefer a URL - we will pick up certificate rollovers automatically.',
  'Production',
  'Provider',
  'Required attribute missing?',
  'Required for IdP-initiated sign-in - identifies your organization',
  'Run a test sign-in to check the attributes your IdP sends',
  "Run a test sign-in to finish setup. Then, right from this page, point each login domain at the connection it should use - domains you don't assign keep password sign-in.",
  'Run another test sign-in',
  'Run test sign-in',
  'SAML claim',
  'SAML claim to map',
  'Sent under a different claim name?',
  'Sign-in method',
  'Sign-in works, but profiles would be incomplete. Fix the mappings below, or adjust the claims in your IdP and run another test.',
  'Single Sign-On',
  "So ExecOnline is recognizable in your company's app launcher, use these when your IdP asks for the application's name and description:",
  'Service provider details',
  'Some attributes need attention',
  'Staging / test',
  'Technical requirements: persistent NameID format (urn:oasis:names:tc:SAML:2.0:nameid-format:persistent), RSA-SHA256 signature algorithm, and a signed assertion.',
  'Test attributes',
  'Test sign-in',
  'Test, then assign domains',
  'The test signs you in through this connection without affecting anyone else - no domains route through it yet.',
  'The values configured in your identity provider application for this connection.',
  'This creates your connection and generates the details your IdP administrator needs in the next step.',
  'Update metadata URL',
  'Updating the metadata URL re-verifies the connection; you will be asked to run a test sign-in again.',
  'Users can only launch ExecOnline once they (or their group) are assigned to the application in your IdP - set up your access groups now so testing is not blocked later.',
  'Verify connection',
  'Verifying metadata',
  'Waiting for your identity provider',
  'Waiting for your test sign-in to finish - complete it in the other tab. This page updates automatically.',
  'We compare what your IdP sent against what ExecOnline expects.',
  'We give you the identifiers, reply URL, and attribute claims your IdP administrator needs.',
  'We will validate the metadata, then take you to a test sign-in that checks the attributes your IdP sends. No one is affected until you assign a domain to this connection.',
  'What your IdP sent',
  'When every required attribute matches, setup completes and you are back on the Single Sign-On page to assign domains.',
  'When your Entra ID application is saved, copy its App Federation Metadata URL (found under SAML Certificates) and paste it here.',
  'When your identity provider application is saved, copy its federation metadata URL and paste it here.',
  'Which identity provider is this?',
  'You will need to add the claim in your IdP and re-test before setup can finish.',
  'Your ExecOnline connection is ready. Set up the application on your side, then paste your metadata URL to finish. These values do not expire - you can come back anytime.',
  'domains assigned',
  'App name',
  'Identifier',
  'Reply URL',
  'Relay state',
  'Username',
  'Your last test sign-in did not complete',
  'The identity provider rejected the sign-in with the following error. Fix the mapping below or adjust the configuration in your IdP, then run another test.',
  'Attributes your IdP needs to send',
  'Configure your IdP to send the SAML claims below for each attribute, then run a test sign-in to check what arrives.',
  'Not tested',
  'Save mapping & re-test',
  'Test results received - the table above shows the latest attributes.',
  // DVLP-7601 — Admin preview
  'Participant view',
  'Show locked courses',
  'Close preview',
  'Nothing to preview',
  'Participants see no courses in this collection.',
  'Participants see no courses in this collection. Its attached courses are hidden because Browse all is turned off.',
  'Learning Collection Preview',
  'Loading preview...',
  'Failed to load preview.',
  'AVAILABLE FOR PARTICIPANTS',
  'NOT INCLUDED IN YOUR CURRENT LICENSES',
  'Show other license options',
  'Hide other license options',
  "You're almost there",
  'This collection requires an active license. Ask your learning administrator to grant you access.',
] as const;

const notTranslatable = [
  '***********',
  'https://www.linkedin.com/in/johndoe',
  'https://www.facebook.com/johndoe',
  'https://www.twitter.com/johndoe',
] as const;

/*
 * Use `{{...}}` to interpolate scalar values (string | number).
 *   Use a `string` type for interpolation when injecting non-translatable content, such as proper nouns, or content that has already been translated externally (string).
 * Use `<abc>...</abc>` to wrap translated content in an element (Interpolator).
 * Use `<xyz/>` to replace with elements that don't need translation content in them (React.ReactElement).
 */
type TParameterizedProps =
  | {
      kind: 'If you need additional help, you can ...';
      emailAddress: React.ReactElement;
    }
  | {
      kind: 'If you need additional help, you can <link>view ...';
      link: Interpolator;
      emailAddress: React.ReactElement;
    }
  | {
      kind: 'Share access to your Learning Collection with <text>authorized users.</text>';
      text: Interpolator;
    }
  | {
      kind: 'Share with <text>collaborators</text>';
      text: Interpolator;
    }
  | {
      kind: 'Publish and share with <text>participants</text>';
      text: Interpolator;
    }
  | { kind: "{{name}} isn't fully licensed"; name: string }
  | { kind: "{{name}} isn't licensed for these courses"; name: string }
  | { kind: "{{withGaps}} person isn't fully licensed"; withGaps: number }
  | {
      kind: "{{withGaps}} of {{total}} people isn't fully licensed";
      withGaps: number;
      total: number;
    }
  | {
      kind: '{{unlicensed}} with no license · {{outsiders}} not in your org';
      unlicensed: number;
      outsiders: number;
    }
  | { kind: "{{excluded}} of them won't receive an invitation."; excluded: number }
  | { kind: "{{withGaps}} people aren't fully licensed"; withGaps: number }
  | {
      kind: "{{withGaps}} of {{total}} people aren't fully licensed";
      withGaps: number;
      total: number;
    }
  | {
      kind: '{{name}} can enroll in {{enrollable}} of {{total}} courses';
      name: string;
      enrollable: number;
      total: number;
    }
  | { kind: 'can enroll in {{enrollable}} of {{total}} courses'; enrollable: number; total: number }
  | { kind: 'Expires {{date}}, before this collection ends'; date: string }
  | { kind: 'Send {{sending}} invitations'; sending: number }
  | { kind: 'Show {{remaining}} more'; remaining: number }
  | { kind: '{{others}} other licenses cover this course.'; others: number }
  | {
      kind: 'Assign licenses on the <peopleLink>People</peopleLink> page.';
      peopleLink: Interpolator;
    }
  | { kind: 'Starts <date/>'; date: React.ReactElement }
  | { kind: 'Started <date/>'; date: React.ReactElement }
  | { kind: 'Expires <date/>'; date: React.ReactElement }
  | { kind: 'Expired <date/>'; date: React.ReactElement }
  | { kind: 'Are you sure you want to delete the section {{name}}? This...'; name: string }
  | { kind: 'You allocated the following license(s) to {{name}}:'; name: string }
  | { kind: '{{count}} week'; count: number }
  | { kind: '{{count}} hour per week'; count: number }
  | { kind: '{{count}} total hour'; count: number }
  | { kind: 'Show locked courses ({{count}})'; count: number }
  | {
      kind: '<span>{{unlocked}}</span> of <span>{{total}}</span> courses unlocked';
      span: Interpolator;
      unlocked: number;
      total: number;
    }
  | { kind: 'Unlocks {{count}} of {{total}} courses'; count: number; total: number }
  | { kind: 'You may allocate licenses to up to {{maxUsers}} users at a time.'; maxUsers: number }
  | {
      kind: 'You may allocate a license to up to {{maxUsers}} users at a time...';
      maxUsers: number;
    }
  | { kind: 'You are over allocated by {{count}} license'; count: number }
  | { kind: '{{count}} participants were imported successfully'; count: number }
  | { kind: '{{count}} participants could not be imported'; count: number }
  | { kind: '{{count}} participant could not be added:'; count: number }
  | { kind: 'Choose from {{count}} start dates'; count: number }
  | { kind: 'Participants choose from {{count}} start dates'; count: number }
  | { kind: '+{{count}} more'; count: number }
  | { kind: 'Include all {{count}} courses in this section'; count: number }
  | { kind: 'Remove all {{count}} courses from this section'; count: number }
  | {
      kind: 'Include all {{count}} {{courseType}} courses in this section';
      count: number;
      courseType: string;
    }
  | {
      kind: 'Remove all {{count}} {{courseType}} courses from this section';
      count: number;
      courseType: string;
    }
  | { kind: 'You allocated licenses to {{count}} participant for'; count: number }
  | { kind: 'Licenses are Over Allocated; there are...'; allocatedCount: number; count: number }
  | {
      kind: 'The selected license has been allocated to...';
      participantsCount: number;
      count: number;
    }
  | {
      kind: '{{organizationName}} purchased {{count}} license...';
      organizationName: string;
      count: number;
      date: React.ReactElement;
    }
  | { kind: '{{count}} day remaining'; count: number }
  | { kind: 'Activations Remaining: {{count}}'; count: number }
  | { kind: 'Selected: ({{count}})'; count: number }
  | { kind: '{{count}} minute from now'; count: number }
  | { kind: '{{count}} minute ago'; count: number }
  | { kind: 'about {{count}} hour from now'; count: number }
  | { kind: 'about {{count}} hour ago'; count: number }
  | { kind: '{{count}} day from now'; count: number }
  | { kind: '{{count}} day ago'; count: number }
  | { kind: '{{count}} month from now'; count: number }
  | { kind: '{{count}} month ago'; count: number }
  | { kind: 'over {{count}} year from now'; count: number }
  | { kind: 'over {{count}} year ago'; count: number }
  | { kind: '{{count}} hour'; count: number }
  | { kind: '{{count}} Hour'; count: number }
  | { kind: '{{count}} Hour_per_week'; count: number }
  | { kind: '{{count}} Program'; count: number }
  | { kind: '{{count}} License Available'; count: number }
  | { kind: '{{count}} day until renewal'; count: number }
  | { kind: 'No License to {{licenseAction}}'; licenseAction: string }
  | {
      kind: 'You {{actionText}} the following license(s) {{preposition}}';
      actionText: string;
      preposition: string;
    }
  | {
      kind: '{{count}} participant(s)';
      count: number;
    }
  | {
      kind: 'Continue with {{count}} Collections';
      count: number;
    }
  | {
      kind: 'This section includes {{count}} programs';
      count: number;
    }
  | {
      kind: 'Your collection has been updated. {{count}} new Program Deep Link can be added for this collection.';
      count: number;
    }
  | {
      kind: 'Your collection has been updated. {{count}} new Course Deep Link can be added for this collection.';
      count: number;
    }
  | {
      kind: 'If this email address is in our database you ...';
      emailAddress: React.ReactElement;
    }
  | { kind: 'You will automatically be logged out in {{count}} second'; count: number }
  | {
      kind: '<link1>Privacy ...';
      link1: Interpolator;
    }
  | {
      kind: '<link1>Terms ...';
      link1: Interpolator;
    }
  | {
      kind: 'Publish <name/> to create a URL...';
      name: React.ReactElement;
    }
  | { kind: 'Licenses Allocated to <name/>'; name: React.ReactElement }
  | { kind: '{{usedCount}} of {{totalCount}}'; usedCount: number; totalCount: number }
  | { kind: 'Page {{currentPage}} of {{pageCount}}'; currentPage: number; pageCount: number }
  | {
      kind: 'Includes {{includedExperienceCount}} of {{totalExperienceCount}} programs';
      includedExperienceCount: number;
      totalExperienceCount: number;
    }
  | {
      kind: 'Includes <span>{{count}} program(s)</span>';
      span: Interpolator;
      count: number;
    }
  | { kind: 'Impersonating by {{emailAddress}}'; emailAddress: string }
  | {
      kind: 'Updating the license will impact the program ...';
      newLicenseName: string;
      span: Interpolator;
    }
  | {
      kind: '<span>{{count}} Curated Collection</span> included';
      span: Interpolator;
      count: number;
    }
  | {
      kind: 'Participants receive <span>Project Feedback</span> from a coach';
      span: Interpolator;
    }
  | {
      kind: 'Participants can access the <span>Essentials catalog</span> with this collection';
      span: Interpolator;
    }
  | {
      kind: '<span>Auto-sync</span> learning design';
      span: Interpolator;
    }
  | {
      kind: '<name/> will be removed from the list ...';
      name: React.ReactElement;
    }
  | {
      kind: 'Copying <name/>';
      name: React.ReactElement;
    }
  | {
      kind: "A copy of <name/> is ready. It's saved as a draft until you publish it.";
      name: React.ReactElement;
    }
  | {
      kind: 'Choose which admins can see and edit <name/>.';
      name: React.ReactElement;
    }
  | {
      kind: '{{count}} Competency';
      count: number;
    }
  | {
      kind: '{{selected}} of {{total}} selected';
      selected: number;
      total: number;
    }
  | {
      kind: 'Viewing {{resultsCount}} of {{totalUnfilteredCount}} programs';
      resultsCount: number;
      totalUnfilteredCount: number;
    }
  | {
      kind: 'Viewing {{resultsCount}} of {{totalCount}} participants';
      resultsCount: number;
      totalCount: number;
    }
  | {
      kind: 'Viewing {{resultsCount}} of {{totalCount}} groups';
      resultsCount: number;
      totalCount: number;
    }
  | { kind: '({{count}} selected)'; count: number }
  | {
      kind: 'Adding to <learningDesign/>';
      learningDesign: React.ReactElement;
    }
  | {
      kind: '{{count}} program';
      count: number;
    }
  | {
      kind: '{{count}} course';
      count: number;
    }
  | {
      kind: 'Discover critical insights, analyze...';
      link: Interpolator;
    }
  | {
      kind: 'Use Learning Collections to curate...';
      link: Interpolator;
    }
  | {
      kind: 'I hit a snag generating the collection...';
      link: Interpolator;
    }
  | {
      kind: 'Program Deep Links for {{name}}';
      name: string;
    }
  | {
      kind: 'Course Deep Links for {{name}}';
      name: string;
    }
  | {
      kind: 'Showing {{displayedCount}} of {{totalCount}}';
      displayedCount: number;
      totalCount: number;
    }
  | { kind: '{{count}} item selected'; count: number }
  | {
      kind: '{{successCount}} of {{totalCount}} participants were added...';
      successCount: number;
      totalCount: number;
      groupName: string;
    }
  | {
      kind: 'Adding "{{name}}"';
      name: string;
    }
  | {
      kind: 'Advanced Leader Programs are scheduled and run 5 times per year in a cohort structure. {{name}} is...';
      name: string;
    }
  | {
      kind: '{{count}} Date';
      count: number;
    }
  | {
      kind: 'CSV file includes {{filteredCount}} out of {{totalCount}} Program Deep Links';
      filteredCount: number;
      totalCount: number;
    }
  | {
      kind: 'Showing {{filteredCount}} out of {{totalCount}} Program Deep Links';
      filteredCount: number;
      totalCount: number;
    }
  | {
      kind: 'Showing {{filteredCount}} out of {{totalCount}} Shareable Links';
      filteredCount: number;
      totalCount: number;
    }
  | {
      kind: 'Show all {{count}} dates';
      count: number;
    }
  | {
      kind: 'Import {{count}} participant';
      count: number;
    }
  | {
      kind: 'Showing {{count}} records for <filename/>';
      count: number;
      filename: React.ReactElement;
    }
  | {
      kind: 'Showing {{successfulCount}} of {{totalCount}} records for <filename/>';
      successfulCount: number;
      totalCount: number;
      filename: React.ReactElement;
    }
  | {
      kind: 'You imported <span>{{successfulCount}} participants</span> ...';
      successfulCount: number;
      totalCount: number;
      viewImport: React.ReactElement;
      span: Interpolator;
    }
  | {
      kind: 'Successfully Imported ...';
      successfulCount: number;
      viewImport: React.ReactElement;
      downloadCSV: React.ReactElement;
      span: Interpolator;
    }
  | {
      kind: 'Already Exist in your Organization ...';
      successfulCount: number;
      viewImport: React.ReactElement;
      downloadCSV: React.ReactElement;
      span: Interpolator;
    }
  | {
      kind: 'Already Exist but in a Different Organization ...';
      successfulCount: number;
      viewImport: React.ReactElement;
      downloadCSV: React.ReactElement;
      span: Interpolator;
    }
  | {
      kind: 'Formatting Issues ...';
      successfulCount: number;
      viewImport: React.ReactElement;
      downloadCSV: React.ReactElement;
      span: Interpolator;
    }
  | {
      kind: 'Import Failed ...';
      successfulCount: number;
      viewImport: React.ReactElement;
      downloadCSV: React.ReactElement;
      span: Interpolator;
    }
  | { kind: 'You added {{count}} new participant to {{name}}.'; count: number; name: string }
  | { kind: 'You added {{count}} participant to {{name}}.'; count: number; name: string }
  | { kind: 'No matches for {{name}}'; name: string }
  | { kind: 'Invite {{email}}'; email: string }
  | { kind: 'Your new group {{name}} has been created.'; name: string }
  | { kind: 'Your new group {{name}} has been edited.'; name: string }
  | {
      kind: '{{count}} Participant';
      count: number;
    }
  | {
      kind: 'Participants to be added ({{count}} selected)';
      count: number;
    }
  | {
      kind: '{{count}} participant will be invited to this collection on <date/> at <time/>.';
      count: number;
      date: React.ReactElement;
      time: React.ReactElement;
    }
  | {
      kind: 'Participant {{participantName}} will be removed from {{groupName}}. Do you want to continue?';
      participantName: string;
      groupName: string;
    }
  | {
      kind: '{{groupName}} has {{count}} group member.';
      groupName: string;
      count: number;
    }
  | {
      kind: 'Participant {{participantName}} removed from {{groupName}}';
      participantName: string;
      groupName: string;
    }
  | {
      kind: 'The {{name}} license associated with this collection is no longer active...';
      name: string;
    }
  | {
      kind: 'However there is only <span>{{count}} license activation</span> remaining for {{name}}.';
      span: Interpolator;
      count: number;
      name: string;
    }
  | {
      kind: 'There is <span>{{count}} license activation</span> remaining for {{name}}.';
      span: Interpolator;
      count: number;
      name: string;
    }
  | {
      kind: '{{name}} will no longer be able to access this learning collection.';
      name: string;
    }
  | {
      kind: '{{name}} will once again be able to access this learning collection.';
      name: string;
    }
  | {
      kind: '{{successCount}} of {{totalCount}} participants will no longer...';
      successCount: number;
      totalCount: number;
    }
  | {
      kind: 'You attempted to import {{totalCount}} participants but only {{successCount}} could be imported successfully';
      successCount: number;
      totalCount: number;
    }
  | {
      kind: '{{successCount}} of {{totalCount}} participants will once again...';
      successCount: number;
      totalCount: number;
    }
  | {
      kind: 'The {{name}} collection is not published. Participants will not have access using the Program Deep Links.';
      name: string;
    }
  | {
      kind: 'The {{name}} collection is not published. Participants will not have access using the Course Deep Links.';
      name: string;
    }
  | {
      kind: 'Customize {{name}}';
      name: string;
    }
  | {
      kind: '<span>{{name}}</span> is a 6 week program offered with the following start dates...';
      name: string;
      span: Interpolator;
    }
  | {
      kind: 'The upcoming faculty-led dates for {{name}} are outside of the license period...';
      name: string;
      expiration: React.ReactElement;
    }
  | {
      kind: 'The upcoming faculty-led dates for {{name}} are outside of the license period...';
      name: string;
      expiration: React.ReactElement;
    }
  | {
      kind: '<span>{{name}}</span> is a faculty-led program offered with the following start dates...';
      name: string;
      span: Interpolator;
    }
  | {
      kind: 'Hello <span>{{name}}</span>';
      name: string;
      span: Interpolator;
    }
  | {
      kind: 'Removed participants lose login access, and free up <span>{{count}} license</span>. Removed participants will continue to appear in reports.';
      count: number;
      span: Interpolator;
    }
  | {
      kind: '<span>This action can only be reversed by an ExecOnline representative. Do you wish to proceed?</span>';
      span: Interpolator;
    }
  | {
      kind: 'If you proceed the selected participants will no longer be able to access programs and collections they were previously invited to. This will return <span>{{count}} previously Activated license</span> to...';
      count: number;
      span: Interpolator;
    }
  | {
      kind: 'You removed access for <span>{{count}} participant</span> who is no longer with your organization.';
      count: number;
      span: Interpolator;
    }
  | {
      kind: 'As a result, <span>{{count}} Activated license</span> was reclaimed for future use.';
      count: number;
      span: Interpolator;
    }
  | {
      kind: 'So far you have not added any <omissions/> programs to this collection';
      omissions: React.ReactElement;
    }
  | {
      kind: 'To get the most out of the license you chose it is recommended to add one or more <omissions/> programs to this collection.';
      omissions: React.ReactElement;
    }
  | {
      kind: 'Potential unlocked! Nice job, this collection includes all types of programs available with your <licenseTitle/> license.';
      licenseTitle: React.ReactElement;
    }
  | {
      kind: '{{utilizationCount}} of {{productTypeCount}} program types included';
      utilizationCount: number;
      productTypeCount: number;
    }
  | {
      kind: 'Your <licenseTitle/> license allows access to the following program types:';
      licenseTitle: React.ReactElement;
    }
  | { kind: 'Active until <date/>'; date: React.ReactElement }
  | {
      kind: 'The {{name}} license associated with this collection has no activations remaining...';
      name: string;
    }
  | {
      kind: 'Expand the developmental experience for your organization with <span>{{name}}</span>...';
      span: Interpolator;
      name: string;
    }
  | { kind: '; joined on <date/>'; date: React.ReactElement }
  | {
      kind: 'ExecOnline offers <span>{{name}}</span> to save you time and help you create collections that drive impact, fast.';

      span: Interpolator;
      name: string;
    }
  | { kind: 'Available in <span>{{languages}}</span>'; languages: string; span: Interpolator }
  | {
      kind: 'Expired <date/> (<span>{{count}} days ago</span>)';
      date: React.ReactElement;
      count: number;
      span: Interpolator;
    }
  | {
      kind: 'Expires <date/> (<span>{{count}} days remaining</span>)';
      date: React.ReactElement;
      count: number;
      span: Interpolator;
    }
  | {
      kind: 'Expires <date/> (<span>{{count}} days from now</span>)';
      date: React.ReactElement;
      count: number;
      span: Interpolator;
    }
  | {
      kind: 'Starts <date/> (<span>{{count}} days from now</span>)';
      date: React.ReactElement;
      count: number;
      span: Interpolator;
    }
  | {
      kind: 'Participants to invite ({{count}})';
      count: number;
    }
  | {
      kind: "Tip: Go to <peopleLink>People</peopleLink> to upgrade participants' licenses if you want them to have full VIP access.";
      peopleLink: Interpolator;
    }
  | {
      kind: 'Tip: Go to <peopleLink>People</peopleLink> to add or import unrecognized participants before inviting them to a collection.';
      peopleLink: Interpolator;
    }
  | {
      kind: 'Configure invitations for the learning collection: <collectionName/>';
      collectionName: React.ReactElement;
    }
  | {
      kind: 'Will be invited to the collection "{{collectionName}}"';
      collectionName: string;
    }
  | {
      kind: 'Configure invitations for the course: <courseName/> in <collectionName/>';
      courseName: React.ReactElement;
      collectionName: React.ReactElement;
    }
  | {
      kind: 'Configure invitations for a course in <collectionName/>';
      collectionName: React.ReactElement;
    }
  | {
      kind: 'Will be invited to the course "{{courseName}}"';
      courseName: string;
    }
  | {
      kind: 'On {{date}}';
      date: string;
    }
  | {
      kind: '<licenseName/> <license/> required for full collection access; participants can only view programs included in their license.';
      licenseName: React.ReactElement;
      license: React.ReactElement;
    }
  // Group & License Selection Sections
  | {
      kind: '{{selectedCount}} of {{totalCount}} selected';
      selectedCount: number;
      totalCount: number;
    }
  | { kind: 'Showing {{count}} •'; count: number }
  | { kind: 'Associated Licenses ({{count}})'; count: number }
  | { kind: 'Associated Collections ({{count}})'; count: number }
  | { kind: '{{count}} license'; count: number }
  | { kind: '{{count}} collection'; count: number }
  | { kind: '{{count}} member'; count: number }
  | { kind: '{{count}} course'; count: number }
  | { kind: '{{count}} license term'; count: number }
  | { kind: 'None available (Over-activated by {{count}})'; count: number }
  | { kind: '{{available}} of {{total}} available'; available: number; total: number }
  | { kind: 'Over-activated by {{overActivated}} ({{overActivated}} of 0)'; overActivated: number }
  | { kind: 'Allocated to: {{groups}}'; groups: string }
  | { kind: 'No groups found matching "{{query}}"'; query: string }
  | { kind: 'No licenses found matching "{{query}}"'; query: string }
  | { kind: 'No collections found matching "{{query}}"'; query: string }
  | {
      kind: 'Configure details, license and collection access for the group: {{groupName}}';
      groupName: string;
    }
  | { kind: 'Create "{{groupName}}"'; groupName: string }
  | { kind: 'Hide {{count}} license term'; count: number }
  | { kind: 'Show {{count}} license term'; count: number }
  | { kind: '{{count}} assigned'; count: number }
  | { kind: '{{count}} allocated'; count: number }
  | { kind: 'May not exceed {{maxLength}} characters'; maxLength: number };

export const parameterizedValues = (
  t: TParameterizedProps,
): Parameterized<TParameterizedKey, TParameterizedProps> => {
  switch (t.kind) {
    case "{{name}} isn't fully licensed":
    case "{{name}} isn't licensed for these courses":
      return p(t, { name: scalar(t.name) });
    case "{{withGaps}} person isn't fully licensed":
      return p(t, { withGaps: scalar(t.withGaps) });
    case "{{withGaps}} of {{total}} people isn't fully licensed":
      return p(t, { withGaps: scalar(t.withGaps), total: scalar(t.total) });
    case '{{unlicensed}} with no license · {{outsiders}} not in your org':
      return p(t, { unlicensed: scalar(t.unlicensed), outsiders: scalar(t.outsiders) });
    case "{{excluded}} of them won't receive an invitation.":
      return p(t, { excluded: scalar(t.excluded) });
    case "{{withGaps}} people aren't fully licensed":
      return p(t, { withGaps: scalar(t.withGaps) });
    case "{{withGaps}} of {{total}} people aren't fully licensed":
      return p(t, { withGaps: scalar(t.withGaps), total: scalar(t.total) });
    case '{{name}} can enroll in {{enrollable}} of {{total}} courses':
      return p(t, {
        name: scalar(t.name),
        enrollable: scalar(t.enrollable),
        total: scalar(t.total),
      });
    case 'can enroll in {{enrollable}} of {{total}} courses':
      return p(t, { enrollable: scalar(t.enrollable), total: scalar(t.total) });
    case 'Expires {{date}}, before this collection ends':
      return p(t, { date: scalar(t.date) });
    case 'Send {{sending}} invitations':
      return p(t, { sending: scalar(t.sending) });
    case 'Show {{remaining}} more':
      return p(t, { remaining: scalar(t.remaining) });
    case '{{others}} other licenses cover this course.':
      return p(t, { others: scalar(t.others) });
    case 'Assign licenses on the <peopleLink>People</peopleLink> page.':
      return p(t, { peopleLink: interpolator(t.peopleLink) });
    case 'Active until <date/>':
    case 'Starts <date/>':
    case 'Started <date/>':
    case 'Expires <date/>':
    case 'Expired <date/>':
    case '; joined on <date/>':
      return p(t, { date: element(t.date) });
    case 'If you need additional help, you can ...':
      return p(t, { emailAddress: element(t.emailAddress) });
    case 'If you need additional help, you can <link>view ...':
      return p(t, { link: interpolator(t.link), emailAddress: element(t.emailAddress) });
    case 'Share access to your Learning Collection with <text>authorized users.</text>':
      return p(t, { text: interpolator(t.text) });
    case 'Share with <text>collaborators</text>':
      return p(t, { text: interpolator(t.text) });
    case 'Publish and share with <text>participants</text>':
      return p(t, { text: interpolator(t.text) });
    case 'The selected license has been allocated to...':
      return p(t, {
        participantsCount: scalar(t.participantsCount),
        count: scalar(t.count),
      });
    case 'Licenses are Over Allocated; there are...':
      return p(t, {
        allocatedCount: scalar(t.allocatedCount),
        count: scalar(t.count),
      });
    case '{{organizationName}} purchased {{count}} license...':
      return p(t, {
        organizationName: scalar(t.organizationName),
        count: scalar(t.count),
        date: element(t.date),
      });
    case 'No License to {{licenseAction}}':
      return p(t, { licenseAction: scalar(t.licenseAction) });
    case 'You {{actionText}} the following license(s) {{preposition}}':
      return p(t, {
        actionText: scalar(t.actionText),
        preposition: scalar(t.preposition),
      });
    case '{{count}} participant(s)':
      return p(t, { count: scalar(t.count) });
    case 'Continue with {{count}} Collections':
      return p(t, { count: scalar(t.count) });
    case 'This section includes {{count}} programs':
      return p(t, { count: scalar(t.count) });
    case 'You may allocate a license to up to {{maxUsers}} users at a time...':
      return p(t, {
        maxUsers: scalar(t.maxUsers),
      });
    case 'You may allocate licenses to up to {{maxUsers}} users at a time.':
      return p(t, {
        maxUsers: scalar(t.maxUsers),
      });
    case 'May not exceed {{maxLength}} characters':
      return p(t, { maxLength: scalar(t.maxLength) });
    case 'Unlocks {{count}} of {{total}} courses':
      return p(t, { count: scalar(t.count), total: scalar(t.total) });
    case '<span>{{unlocked}}</span> of <span>{{total}}</span> courses unlocked':
      return p(t, {
        span: interpolator(t.span),
        unlocked: scalar(t.unlocked),
        total: scalar(t.total),
      });
    case 'You are over allocated by {{count}} license':
    case '{{count}} week':
    case '{{count}} total hour':
    case '{{count}} hour per week':
    case 'Show locked courses ({{count}})':
    case 'You allocated licenses to {{count}} participant for':
    case '{{count}} day remaining':
    case 'Activations Remaining: {{count}}':
    case '{{count}} minute from now':
    case '{{count}} minute ago':
    case 'about {{count}} hour from now':
    case 'You will automatically be logged out in {{count}} second':
    case 'about {{count}} hour ago':
    case '{{count}} day from now':
    case '{{count}} day ago':
    case 'Selected: ({{count}})':
    case '{{count}} month from now':
    case '{{count}} month ago':
    case 'over {{count}} year from now':
    case 'over {{count}} year ago':
    case '{{count}} hour':
    case '{{count}} Hour':
    case '{{count}} Hour_per_week':
    case '{{count}} Program':
    case '{{count}} Competency':
    case '{{count}} program':
    case '{{count}} course':
    case 'Your collection has been updated. {{count}} new Program Deep Link can be added for this collection.':
    case 'Your collection has been updated. {{count}} new Course Deep Link can be added for this collection.':
    case '{{count}} item selected':
    case '{{count}} Date':
    case 'Show all {{count}} dates':
    case 'Import {{count}} participant':
    case '{{count}} Participant':
    case 'Participants to be added ({{count}} selected)':
    case '{{count}} License Available':
    case '{{count}} day until renewal':
    case '{{count}} participants were imported successfully':
    case '{{count}} participants could not be imported':
    case '{{count}} participant could not be added:':
    case 'Choose from {{count}} start dates':
    case 'Participants choose from {{count}} start dates':
    case '+{{count}} more':
    case 'Include all {{count}} courses in this section':
    case 'Remove all {{count}} courses from this section':
      return p(t, { count: scalar(t.count) });
    case 'Include all {{count}} {{courseType}} courses in this section':
    case 'Remove all {{count}} {{courseType}} courses from this section':
      return p(t, { count: scalar(t.count), courseType: scalar(t.courseType) });
    case 'CSV file includes {{filteredCount}} out of {{totalCount}} Program Deep Links':
    case 'Showing {{filteredCount}} out of {{totalCount}} Program Deep Links':
    case 'Showing {{filteredCount}} out of {{totalCount}} Shareable Links':
      return p(t, { filteredCount: scalar(t.filteredCount), totalCount: scalar(t.totalCount) });
    case 'If this email address is in our database you ...':
      return p(t, { emailAddress: element(t.emailAddress) });
    case '<link1>Privacy ...':
    case '<link1>Terms ...':
      return p(t, {
        link1: interpolator(t.link1),
      });
    case 'Publish <name/> to create a URL...':
    case 'Licenses Allocated to <name/>':
    case '<name/> will be removed from the list ...':
    case 'Copying <name/>':
    case "A copy of <name/> is ready. It's saved as a draft until you publish it.":
    case 'Choose which admins can see and edit <name/>.':
      return p(t, {
        name: element(t.name),
      });
    case '{{usedCount}} of {{totalCount}}':
      return p(t, {
        usedCount: scalar(t.usedCount),
        totalCount: scalar(t.totalCount),
      });
    case '{{selected}} of {{total}} selected':
      return p(t, {
        selected: scalar(t.selected),
        total: scalar(t.total),
      });
    case 'Page {{currentPage}} of {{pageCount}}':
      return p(t, {
        currentPage: scalar(t.currentPage),
        pageCount: scalar(t.pageCount),
      });
    case 'Includes {{includedExperienceCount}} of {{totalExperienceCount}} programs':
      return p(t, {
        includedExperienceCount: scalar(t.includedExperienceCount),
        totalExperienceCount: scalar(t.totalExperienceCount),
      });
    case 'Includes <span>{{count}} program(s)</span>':
    case 'Removed participants lose login access, and free up <span>{{count}} license</span>. Removed participants will continue to appear in reports.':
    case 'If you proceed the selected participants will no longer be able to access programs and collections they were previously invited to. This will return <span>{{count}} previously Activated license</span> to...':
    case 'You removed access for <span>{{count}} participant</span> who is no longer with your organization.':
    case 'As a result, <span>{{count}} Activated license</span> was reclaimed for future use.':
      return p(t, {
        span: interpolator(t.span),
        count: scalar(t.count),
      });
    case 'Invite {{email}}':
      return p(t, { email: scalar(t.email) });
    case 'Impersonating by {{emailAddress}}':
      return p(t, { emailAddress: scalar(t.emailAddress) });
    case 'Updating the license will impact the program ...':
      return p(t, { newLicenseName: scalar(t.newLicenseName), span: interpolator(t.span) });
    case '<span>{{count}} Curated Collection</span> included':
      return p(t, { span: interpolator(t.span), count: scalar(t.count) });
    case 'Participants receive <span>Project Feedback</span> from a coach':
    case 'Participants can access the <span>Essentials catalog</span> with this collection':
    case '<span>Auto-sync</span> learning design':
    case '<span>This action can only be reversed by an ExecOnline representative. Do you wish to proceed?</span>':
      return p(t, { span: interpolator(t.span) });
    case 'Viewing {{resultsCount}} of {{totalUnfilteredCount}} programs':
      return p(t, {
        resultsCount: scalar(t.resultsCount),
        totalUnfilteredCount: scalar(t.totalUnfilteredCount),
      });
    case 'Viewing {{resultsCount}} of {{totalCount}} participants':
    case 'Viewing {{resultsCount}} of {{totalCount}} groups':
      return p(t, { resultsCount: scalar(t.resultsCount), totalCount: scalar(t.totalCount) });
    case '({{count}} selected)':
      return p(t, { count: scalar(t.count) });
    case 'Adding to <learningDesign/>':
      return p(t, {
        learningDesign: element(t.learningDesign),
      });
    case 'Discover critical insights, analyze...':
      return p(t, {
        link: interpolator(t.link),
      });
    case 'Use Learning Collections to curate...':
      return p(t, {
        link: interpolator(t.link),
      });
    case 'I hit a snag generating the collection...':
      return p(t, {
        link: interpolator(t.link),
      });
    case 'Are you sure you want to delete the section {{name}}? This...':
    case 'You allocated the following license(s) to {{name}}:':
    case 'Program Deep Links for {{name}}':
    case 'Course Deep Links for {{name}}':
    case 'Adding "{{name}}"':
    case 'Advanced Leader Programs are scheduled and run 5 times per year in a cohort structure. {{name}} is...':
    case 'No matches for {{name}}':
    case 'The {{name}} license associated with this collection is no longer active...':
    case 'The {{name}} collection is not published. Participants will not have access using the Program Deep Links.':
    case 'The {{name}} collection is not published. Participants will not have access using the Course Deep Links.':
    case 'Customize {{name}}':
      return p(t, { name: scalar(t.name) });
    case 'Showing {{displayedCount}} of {{totalCount}}':
      return p(t, {
        displayedCount: scalar(t.displayedCount),
        totalCount: scalar(t.totalCount),
      });
    case '{{successCount}} of {{totalCount}} participants were added...':
      return p(t, {
        successCount: scalar(t.successCount),
        totalCount: scalar(t.totalCount),
        groupName: scalar(t.totalCount),
      });
    case 'Showing {{count}} records for <filename/>':
      return p(t, {
        count: scalar(t.count),
        filename: element(t.filename),
      });
    case 'Showing {{successfulCount}} of {{totalCount}} records for <filename/>':
      return p(t, {
        successfulCount: scalar(t.successfulCount),
        totalCount: scalar(t.totalCount),
        filename: element(t.filename),
      });
    case 'You imported <span>{{successfulCount}} participants</span> ...':
      return p(t, {
        span: interpolator(t.span),
        successfulCount: scalar(t.successfulCount),
        totalCount: scalar(t.totalCount),
        viewImport: element(t.viewImport),
      });
    case 'Successfully Imported ...':
    case 'Already Exist but in a Different Organization ...':
    case 'Already Exist in your Organization ...':
    case 'Formatting Issues ...':
    case 'Import Failed ...':
      return p(t, {
        span: interpolator(t.span),
        successfulCount: scalar(t.successfulCount),
        viewImport: element(t.viewImport),
        downloadCSV: element(t.downloadCSV),
      });
    case 'However there is only <span>{{count}} license activation</span> remaining for {{name}}.':
    case 'There is <span>{{count}} license activation</span> remaining for {{name}}.':
      return p(t, {
        span: interpolator(t.span),
        count: scalar(t.count),
        name: scalar(t.name),
      });
    case 'Hello <span>{{name}}</span>':
    case 'ExecOnline offers <span>{{name}}</span> to save you time and help you create collections that drive impact, fast.':
    case '<span>{{name}}</span> is a 6 week program offered with the following start dates...':
    case 'Expand the developmental experience for your organization with <span>{{name}}</span>...':
    case '<span>{{name}}</span> is a faculty-led program offered with the following start dates...':
      return p(t, {
        span: interpolator(t.span),
        name: scalar(t.name),
      });
    case 'You added {{count}} new participant to {{name}}.':
    case 'You added {{count}} participant to {{name}}.':
      return p(t, { name: scalar(t.name), count: scalar(t.count) });
    case 'Your new group {{name}} has been created.':
      return p(t, { name: scalar(t.name) });
    case 'Your new group {{name}} has been edited.':
      return p(t, { name: scalar(t.name) });
    case '{{count}} participant will be invited to this collection on <date/> at <time/>.':
      return p(t, { count: scalar(t.count), date: element(t.date), time: element(t.time) });
    case 'Participant {{participantName}} will be removed from {{groupName}}. Do you want to continue?':
      return p(t, { participantName: scalar(t.participantName), groupName: scalar(t.groupName) });
    case '{{groupName}} has {{count}} group member.':
      return p(t, { groupName: scalar(t.groupName), count: scalar(t.count) });
    case 'Participant {{participantName}} removed from {{groupName}}':
      return p(t, { participantName: scalar(t.participantName), groupName: scalar(t.groupName) });
    case '{{name}} will no longer be able to access this learning collection.':
      return p(t, { name: scalar(t.name) });
    case '{{name}} will once again be able to access this learning collection.':
      return p(t, { name: scalar(t.name) });
    case '{{successCount}} of {{totalCount}} participants will no longer...':
      return p(t, {
        successCount: scalar(t.successCount),
        totalCount: scalar(t.totalCount),
      });
    case 'You attempted to import {{totalCount}} participants but only {{successCount}} could be imported successfully':
    case '{{successCount}} of {{totalCount}} participants will once again...':
      return p(t, {
        successCount: scalar(t.successCount),
        totalCount: scalar(t.totalCount),
      });
    case 'The upcoming faculty-led dates for {{name}} are outside of the license period...':
      return p(t, {
        name: scalar(t.name),
        expiration: element(t.expiration),
      });
    case 'So far you have not added any <omissions/> programs to this collection':
      return p(t, {
        omissions: element(t.omissions),
      });
    case 'To get the most out of the license you chose it is recommended to add one or more <omissions/> programs to this collection.':
      return p(t, {
        omissions: element(t.omissions),
      });
    case 'Your <licenseTitle/> license allows access to the following program types:':
    case 'Potential unlocked! Nice job, this collection includes all types of programs available with your <licenseTitle/> license.':
      return p(t, {
        licenseTitle: element(t.licenseTitle),
      });
    case '{{utilizationCount}} of {{productTypeCount}} program types included':
      return p(t, {
        utilizationCount: scalar(t.utilizationCount),
        productTypeCount: scalar(t.productTypeCount),
      });
    case 'The {{name}} license associated with this collection has no activations remaining...':
      return p(t, {
        name: scalar(t.name),
      });
    case 'Available in <span>{{languages}}</span>':
      return p(t, { span: interpolator(t.span), languages: scalar(t.languages) });
    case 'Expired <date/> (<span>{{count}} days ago</span>)':
      return p(t, { count: scalar(t.count), date: element(t.date), span: interpolator(t.span) });
    case 'Expires <date/> (<span>{{count}} days remaining</span>)':
      return p(t, { count: scalar(t.count), date: element(t.date), span: interpolator(t.span) });
    case 'Expires <date/> (<span>{{count}} days from now</span>)':
      return p(t, { count: scalar(t.count), date: element(t.date), span: interpolator(t.span) });
    case 'Starts <date/> (<span>{{count}} days from now</span>)':
      return p(t, { count: scalar(t.count), date: element(t.date), span: interpolator(t.span) });
    case 'Participants to invite ({{count}})':
      return p(t, { count: scalar(t.count) });
    case "Tip: Go to <peopleLink>People</peopleLink> to upgrade participants' licenses if you want them to have full VIP access.":
      return p(t, { peopleLink: interpolator(t.peopleLink) });
    case 'Tip: Go to <peopleLink>People</peopleLink> to add or import unrecognized participants before inviting them to a collection.':
      return p(t, { peopleLink: interpolator(t.peopleLink) });
    case 'Configure invitations for the learning collection: <collectionName/>':
      return p(t, { collectionName: element(t.collectionName) });
    case 'Will be invited to the collection "{{collectionName}}"':
      return p(t, { collectionName: scalar(t.collectionName) });
    case 'Configure invitations for the course: <courseName/> in <collectionName/>':
      return p(t, { courseName: element(t.courseName), collectionName: element(t.collectionName) });
    case 'Configure invitations for a course in <collectionName/>':
      return p(t, { collectionName: element(t.collectionName) });
    case 'Will be invited to the course "{{courseName}}"':
      return p(t, { courseName: scalar(t.courseName) });
    case 'On {{date}}':
      return p(t, { date: scalar(t.date) });
    case '<licenseName/> <license/> required for full collection access; participants can only view programs included in their license.':
      return p(t, { licenseName: element(t.licenseName), license: element(t.license) });
    case '{{selectedCount}} of {{totalCount}} selected':
      return p(t, { selectedCount: scalar(t.selectedCount), totalCount: scalar(t.totalCount) });
    case 'Showing {{count}} •':
    case 'Associated Licenses ({{count}})':
    case 'Associated Collections ({{count}})':
    case '{{count}} license':
    case '{{count}} collection':
    case '{{count}} member':
    case '{{count}} license term':
    case 'None available (Over-activated by {{count}})':
    case 'Hide {{count}} license term':
    case 'Show {{count}} license term':
      return p(t, { count: scalar(t.count) });
    case '{{available}} of {{total}} available':
      return p(t, { available: scalar(t.available), total: scalar(t.total) });
    case 'Over-activated by {{overActivated}} ({{overActivated}} of 0)':
      return p(t, { overActivated: scalar(t.overActivated) });
    case 'Allocated to: {{groups}}':
      return p(t, { groups: scalar(t.groups) });
    case 'No groups found matching "{{query}}"':
    case 'No licenses found matching "{{query}}"':
    case 'No collections found matching "{{query}}"':
      return p(t, { query: scalar(t.query) });
    case 'Configure details, license and collection access for the group: {{groupName}}':
      return p(t, { groupName: scalar(t.groupName) });
    case 'Create "{{groupName}}"':
      return p(t, { groupName: scalar(t.groupName) });
    case '{{count}} assigned':
    case '{{count}} allocated':
      return p(t, { count: scalar(t.count) });
  }
};

type TParameterizedKey = TParameterizedProps['kind'];
type TNotTranslatable = (typeof notTranslatable)[number];
type TTranslatablePlainTextKey = (typeof translatablePlainTextKeys)[number];

export type TPlainTextKey = TTranslatablePlainTextKey | TNotTranslatable;
export type TProps = Props<TPlainTextKey, TParameterizedKey, TParameterizedProps>;

export const whenTPlainTextKey = (candidate: string): Maybe<TPlainTextKey> => {
  const whenStringIncludedIn = find<TPlainTextKey>(equalTo(candidate));

  return whenStringIncludedIn(notTranslatable).orElse(() =>
    whenStringIncludedIn(translatablePlainTextKeys),
  );
};

export const { L, translation, translator, T } = translations<
  TPlainTextKey,
  TNotTranslatable,
  TParameterizedKey,
  TParameterizedProps
>(translatablePlainTextKeys, notTranslatable, parameterizedValues);

export {
  AlreadyTranslated,
  alreadyTranslatedText,
  localizer,
  NotTranslated,
  TranslationsContext,
  TranslationsLoader,
} from '@execonline-inc/translations';
export type {
  AlreadyTranslatedText,
  LocalizationFormat,
  TranslationsState,
} from '@execonline-inc/translations';

export interface NotYetTranslatedText {
  kind: 'not-yet-translated-text';
  translationKey: TPlainTextKey;
}

export const notYetTranslatedText = (translationKey: TPlainTextKey): NotYetTranslatedText => ({
  kind: 'not-yet-translated-text',
  translationKey,
});
